// src/pages/Donate/Donate.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Switch,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { donationService } from '../../services/api/donationService';
import { WhiteBackgroundWrapper } from './Donate.styles';

const Donate: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [totalDonations, setTotalDonations] = useState<number>(0);
  const [totalDonors, setTotalDonors] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      setMessage({ text: 'Thank you for your generous donation!', severity: 'success' });
    } else if (params.get('canceled') === 'true') {
      setMessage({ text: "Donation was canceled. Please try again when you're ready.", severity: 'error' });
    }
  }, [location.search]);

  // Donation/donor counts are admin-only stats — not fetched on the public page
  useEffect(() => {
    setTotalDonations(0);
    setTotalDonors(0);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validate form fields
    if (!firstName || !lastName || !email || !amount || Number(amount) <= 0) {
      setMessage({ text: 'Please fill in all fields with valid information.', severity: 'error' });
      return;
    }

    try {
      setLoading(true);

      // Create a Stripe Checkout Session with the donation details
      const stripeUrl = await donationService.createCheckoutSession({
        firstName,
        lastName,
        email,
        amount: Number(amount),
        isRecurring,
      });

      // Redirect the user to the Stripe Checkout page
      window.location.href = stripeUrl;
    } catch (err: any) {
      setMessage({ text: err.message || 'Failed to process donation', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundImage: 'url(/images/donate/donate_002.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 5,
      }}
    >
      <WhiteBackgroundWrapper>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          Join Our Mission
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary">
          Every donation makes a difference! Become a part of our growing community.
        </Typography>

        {message && (
          <Alert severity={message.severity} sx={{ mt: 3 }}>
            {message.text}
          </Alert>
        )}

        {/* Toggle between One-time and Recurring Donations */}
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
          <Typography variant="body1">One-time</Typography>
          <Switch checked={isRecurring} onChange={() => setIsRecurring(!isRecurring)} />
          <Typography variant="body1">Recurring</Typography>
        </Box>

        {/* Donation Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Donation Amount (USD)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                required
                inputProps={{ min: 1, step: 0.01 }}
              />
            </Grid>
          </Grid>

          {/* Donation Type Card - Dynamic Message Based on Selection */}
          <Card sx={{ mt: 3, p: 2, textAlign: 'center', bgcolor: isRecurring ? '#e3f2fd' : '#fbe9e7' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">
                {isRecurring ? 'Recurring Donation' : 'One-time Donation'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isRecurring
                  ? 'Your donation will be automatically processed each month to provide ongoing support.'
                  : 'Your generous one-time donation will have an immediate impact.'}
              </Typography>
            </CardContent>
          </Card>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 4, py: 1.5, fontSize: '1.1rem' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Donate Now'}
          </Button>
        </Box>

        {/* Display real-time donation count & donor count */}
        <Card sx={{ mt: 3, p: 3, textAlign: 'center', bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Total Donations
            </Typography>
            <Typography variant="h4" color="secondary" fontWeight="bold">
              {totalDonations.toLocaleString()}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mt: 2 }}>
              Total Donors
            </Typography>
            <Typography variant="h4" color="secondary" fontWeight="bold">
              {totalDonors.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </WhiteBackgroundWrapper>
    </Box>
  );
};

export default Donate;
